import { FC, PropsWithChildren, ReactElement, ReactNode, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link, useLocation } from 'react-router'

import { Breadcrumb, Flex, Layout, Spin } from 'antd'

import { Context as SeoContext } from '../../providers/Seo'
import { ScrollRestoration } from '../layout/ScrollRestoration'
import { Title } from '../text/Title'

const Page: FC<
  PropsWithChildren<{
    title?: ReactNode
    breadcrumbs?: { title: ReactElement }[]
    loading?: boolean
    seo?: Partial<SeoFragment> | null
    extra?: ReactNode
  }>
> = ({
  children,
  breadcrumbs = [],
  loading = false,
  title = '',
  seo = { metaTitle: '', metaDescription: '', keywords: '' },
  extra,
}) => {
  const { pathname } = useLocation()
  const { seo: pageSeo } = useContext(SeoContext)
  const data = pageSeo?.find((item) => item?.uri === pathname)

  const items = title
    ? [
        {
          title: (
            <Link to={'/'}>
              <FormattedMessage id={'header.navigation.home'} />
            </Link>
          ),
        },
      ].concat(...breadcrumbs)
    : []

  const metaTitle = data?.metaTitle ?? seo?.metaTitle ?? title
  const metaDescription = data?.metaDescription ?? seo?.metaDescription
  const keywords = data?.keywords ?? seo?.keywords

  return (
    <Layout>
      {metaTitle && <title>{metaTitle}</title>}
      {metaDescription && <meta name={'description'} content={metaDescription} />}
      {keywords && <meta name={'keywords'} content={keywords} />}
      <ScrollRestoration />
      <Flex vertical gap={16}>
        {loading || (items.length > 1 && <Breadcrumb items={items} />)}
        {title || extra ? (
          <Flex justify={'space-between'} vertical>
            <Title>{title}</Title>
            {loading || extra}
          </Flex>
        ) : null}
        <Layout.Content>{loading ? <Spin size={'small'} fullscreen /> : children}</Layout.Content>
      </Flex>
    </Layout>
  )
}

export { Page }
